import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDirectory, type DirectoryEmployee } from '../api/client';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../components/Icon';
import { Pending } from './org/shared';

type Quick = '' | 'active' | 'remote' | 'office' | 'hybrid' | 'intern' | 'former' | 'wfh_requested' | 'incomplete' | 'no_jd';

const QUICK: Array<{ key: Quick; label: string }> = [
  { key: '', label: 'Everyone' },
  { key: 'active', label: 'Active' },
  { key: 'remote', label: 'Remote / WFH' },
  { key: 'office', label: 'Office' },
  { key: 'hybrid', label: 'Hybrid' },
  { key: 'intern', label: 'Intern' },
  { key: 'wfh_requested', label: 'WFH requested' },
  { key: 'incomplete', label: 'Missing information' },
  { key: 'no_jd', label: 'Missing JD' },
  { key: 'former', label: 'Former employees' },
];

function JdBadge({ row }: { row: DirectoryEmployee }) {
  if (row.jdStatus === 'active') return <span className="org-chip org-chip-active">JD {row.jdVersion}</span>;
  if (row.jdStatus === 'draft') return <span className="org-chip org-chip-draft">JD draft</span>;
  return <span className="org-chip org-chip-pending">No JD</span>;
}

function ArrangementCell({ row }: { row: DirectoryEmployee }) {
  // Requested is never shown as though it were approved.
  if (row.workArrangementRequest && row.workArrangementRequest !== 'approved') {
    return (
      <span>
        <span className="org-chip org-chip-requested">{row.requestedArrangement ?? 'change'} requested</span>
        <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
          Currently {row.workArrangement ?? 'unconfirmed'}
        </div>
      </span>
    );
  }
  return row.workArrangement
    ? <span className="org-chip org-chip-quiet">{row.workArrangement}</span>
    : <Pending />;
}

export function Employees() {
  const { can } = useAuth();
  const [rows, setRows] = useState<DirectoryEmployee[]>([]);
  const [departments, setDepartments] = useState<Array<{ id: string; code: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [quick, setQuick] = useState<Quick>('active');
  const [status, setStatus] = useState('');
  const [department, setDepartment] = useState('');
  const [designation, setDesignation] = useState('');
  const [manager, setManager] = useState('');
  const [arrangement, setArrangement] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [jdStatus, setJdStatus] = useState('');
  const [kpiStatus, setKpiStatus] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showDemo, setShowDemo] = useState(false);

  useEffect(() => {
    getDirectory()
      .then((r) => { setRows(r.employees); setDepartments(r.departments); setError(null); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const designations = useMemo(
    () => [...new Set(rows.map((r) => r.designation).filter(Boolean))].sort() as string[], [rows]);
  const managers = useMemo(
    () => [...new Map(rows.filter((r) => r.managerId).map((r) => [r.managerId!, r.managerName!])).entries()]
      .sort((a, b) => a[1].localeCompare(b[1])), [rows]);
  const types = useMemo(() => [...new Set(rows.map((r) => r.employmentType))].sort(), [rows]);

  const demoHidden = useMemo(
    () => rows.filter((r) => r.dataClassification === 'demo').length, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      // Only rows explicitly labelled demo are hidden. An unclassified row is
      // never dropped from a governance view on a guess.
      .filter((r) => showDemo || r.dataClassification !== 'demo')
      .filter((r) => {
        if (!q) return true;
        return [r.name, r.employeeCode, r.designation ?? '', r.department ?? '']
          .some((v) => v.toLowerCase().includes(q));
      })
      .filter((r) => {
        switch (quick) {
          case 'active': return r.status !== 'exited';
          case 'former': return r.status === 'exited';
          case 'remote': return r.workArrangement === 'remote';
          case 'office': return r.workArrangement === 'office';
          case 'hybrid': return r.workArrangement === 'hybrid';
          case 'intern': return /intern/i.test(r.employmentType) || /intern/i.test(r.designation ?? '');
          case 'wfh_requested': return Boolean(r.workArrangementRequest) && r.workArrangementRequest !== 'approved';
          case 'incomplete': return r.openItems > 0;
          case 'no_jd': return r.jdStatus === 'none';
          default: return true;
        }
      })
      .filter((r) => !status || r.status === status)
      .filter((r) => !department || r.departmentId === department)
      .filter((r) => !designation || r.designation === designation)
      .filter((r) => !manager || r.managerId === manager)
      .filter((r) => !arrangement || r.workArrangement === arrangement)
      .filter((r) => !employmentType || r.employmentType === employmentType)
      .filter((r) => !jdStatus || r.jdStatus === jdStatus)
      .filter((r) => !kpiStatus || r.kpiStatus === kpiStatus)
      .filter((r) => !confirmation
        || (confirmation === 'complete' ? r.openItems === 0 : r.openItems > 0))
      .sort((a, b) => a.employeeCode.localeCompare(b.employeeCode));
  }, [rows, search, quick, status, department, designation, manager, arrangement,
      employmentType, jdStatus, kpiStatus, confirmation, showDemo]);

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow"><Icon name="employees" size="xs" /><span>Employee Directory</span></span>
          <h1>Staff &amp; Workforce</h1>
        </div>
        <div className="toolbar">
          <input
            aria-label="Search name, code, designation or department"
            placeholder="Search name, code, designation, department…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {can('employee.create') && (
            <button className="btn-primary"><Icon name="userPlus" size="xs" /><span>Add Employee</span></button>
          )}
        </div>
      </div>

      {error && <div className="banner error" style={{ marginBottom: 14 }}>{error}</div>}

      <div className="org-quickbar">
        {QUICK.map((q) => (
          <button
            key={q.key || 'all'}
            type="button"
            className={`org-quick${quick === q.key ? ' org-quick-on' : ''}`}
            onClick={() => setQuick(q.key)}
          >
            {q.label}
          </button>
        ))}
      </div>

      <div className="panel">
        <div className="panel-head">
          <strong>{loading ? 'Loading workforce records…' : `${filtered.length} of ${rows.length} shown`}</strong>
          <span className="muted" style={{ fontSize: 12 }}>Role-scoped visibility active</span>
        </div>

        <div className="org-filters">
          <select aria-label="Employment status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any status</option>
            <option value="active">Active</option><option value="probation">Probation</option>
            <option value="notice">Notice</option><option value="exited">Exited</option>
          </select>
          <select aria-label="Department" value={department} onChange={(e) => setDepartment(e.target.value)}>
            <option value="">Any department</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select aria-label="Designation" value={designation} onChange={(e) => setDesignation(e.target.value)}>
            <option value="">Any designation</option>
            {designations.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select aria-label="Reporting manager" value={manager} onChange={(e) => setManager(e.target.value)}>
            <option value="">Any manager</option>
            {managers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select aria-label="Work arrangement" value={arrangement} onChange={(e) => setArrangement(e.target.value)}>
            <option value="">Any arrangement</option>
            <option value="office">Office</option><option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option><option value="management">Management</option>
          </select>
          <select aria-label="Employment type" value={employmentType} onChange={(e) => setEmploymentType(e.target.value)}>
            <option value="">Any type</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select aria-label="JD status" value={jdStatus} onChange={(e) => setJdStatus(e.target.value)}>
            <option value="">Any JD status</option>
            <option value="active">JD published</option><option value="draft">JD draft</option><option value="none">No JD</option>
          </select>
          <select aria-label="KPI status" value={kpiStatus} onChange={(e) => setKpiStatus(e.target.value)}>
            <option value="">Any KPI status</option>
            <option value="defined">KPIs defined</option><option value="pending">KPIs pending</option>
          </select>
          <select aria-label="Confirmation status" value={confirmation} onChange={(e) => setConfirmation(e.target.value)}>
            <option value="">Any confirmation state</option>
            <option value="complete">Complete</option><option value="incomplete">Incomplete</option>
          </select>
        </div>

        {demoHidden > 0 && (
          <div className="banner info" style={{ margin: '0 0 12px' }}>
            {showDemo
              ? `Showing ${demoHidden} record${demoHidden === 1 ? '' : 's'} labelled demo data.`
              : `${demoHidden} record${demoHidden === 1 ? '' : 's'} labelled demo data ${demoHidden === 1 ? 'is' : 'are'} hidden. Nothing has been deleted.`}
            <button type="button" style={{ marginLeft: 'auto' }} onClick={() => setShowDemo((v) => !v)}>
              {showDemo ? 'Hide demo data' : 'Show demo data'}
            </button>
          </div>
        )}

        {!loading && filtered.length === 0 ? (
          <div className="empty">
            <strong>No employees match these filters</strong>
            <p>Clear a filter, or switch the quick filter back to Everyone.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Code</th>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Reports to</th>
                  <th style={{ width: 160 }}>Arrangement</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 110 }}>JD</th>
                  <th style={{ width: 130 }}>Confirmation</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.employeeId}>
                    <td><Link className="record-link mono" to={`/employees/${r.employeeId}`}>{r.employeeCode}</Link></td>
                    <td>
                      <strong>{r.name}</strong>
                      <div className="muted" style={{ fontSize: 12 }}><Pending>{r.designation ?? undefined}</Pending></div>
                    </td>
                    <td className="muted">{r.department ?? <Pending />}</td>
                    <td className="muted">
                      {r.managerName
                        ?? (r.status === 'exited' || !r.gaps.includes('reporting_manager')
                          ? <span title="Top of the reporting chain by design">—</span>
                          : <Pending />)}
                    </td>
                    <td><ArrangementCell row={r} /></td>
                    <td><StatusBadge status={r.status} /></td>
                    <td><JdBadge row={r} /></td>
                    <td>
                      {r.openItems === 0
                        ? <span className="org-chip org-chip-active">Complete</span>
                        : <span className="org-chip org-chip-pending">{r.openItems} open</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
